import { useTranslation } from 'react-i18next';
import { FormFieldRowRequirement, FormFieldRowValue } from '@components/form-field-row';
import { FormFieldTable, FormFieldTableRow } from '@components/form-field-table';
import { LabelInfo } from '@components/label/src/label-info';
import { SectionDisclosure } from '@components/section-disclosure';
import { ToggleSection } from '@components/toggle-section';
import {
  TOGGLE_SECTION_SIZE_L,
  TOGGLE_SECTION_SIZE_S,
  ToggleSectionSize,
} from '@components/toggle-section/src/toggle-section.type';
import { ADMISSION_DRAWER_OVERLAY_Z_INDEX, ADMISSION_DRAWER_SURFACE_CLASSNAME } from '@constants/admission-drawer';
import { SM_VIEWPORT } from '@constants/viewport';
import { FormFieldPreset } from '@domain/form-field-presets';
import { useWindowSize } from '@hooks/use-window-size';

import { FormFieldGroupValue, FormFieldsSectionCustomField, FormFieldsSectionProps } from './form-fields-section.type';

export const FORM_FIELDS_SECTION_TEST_ID = 'form-fields-section';

const SLUG_FIRST_NAME = 'firstName';
const SLUG_LAST_NAME = 'lastName';
const SLUG_EMAIL = 'email';
const SLUG_PHONE = 'phone';

const TICKET_HOLDER_LOCKED_SLUGS: ReadonlySet<string> = new Set();

type GroupKey = 'mainHolder' | 'ticketHolder';

const nextCustomFieldId = () => `custom-${globalThis.crypto.randomUUID()}`;

const tid = (prefix: string, suffix: string) => `${prefix}-${suffix}`;

interface BuildRowsArgs {
  group: FormFieldGroupValue;
  presets: FormFieldPreset[];
  nameAndSurnameLabel: string;
  emailLabel: string;
  phoneLabel: string;
  lockedSlugs: ReadonlySet<string>;
}

const buildRows = ({
  group,
  presets,
  nameAndSurnameLabel,
  emailLabel,
  phoneLabel,
  lockedSlugs,
}: BuildRowsArgs): FormFieldTableRow[] => {
  const lockedRows: FormFieldTableRow[] = [
    { id: 'locked-name', kind: 'locked', label: nameAndSurnameLabel, requirement: 'required' },
  ];
  if (lockedSlugs.has(SLUG_EMAIL)) {
    lockedRows.push({ id: 'locked-email', kind: 'locked', label: emailLabel, requirement: 'required' });
  }
  if (lockedSlugs.has(SLUG_PHONE)) {
    lockedRows.push({ id: 'locked-phone', kind: 'locked', label: phoneLabel, requirement: 'required' });
  }

  const visiblePresets = presets.filter(
    (preset) => preset.slug !== SLUG_FIRST_NAME && preset.slug !== SLUG_LAST_NAME && !lockedSlugs.has(preset.slug)
  );

  const presetRows: FormFieldTableRow[] = visiblePresets.map((preset) => ({
    id: `preset-${preset.slug}`,
    kind: 'preset',
    label: preset.name,
    requirement: group.presetRequirements[preset.slug] ?? 'off',
  }));

  const customRows: FormFieldTableRow[] = group.customFields.map((custom) => ({
    id: custom.id,
    kind: 'custom',
    value: custom.value,
    requirement: custom.requirement,
  }));

  return [...lockedRows, ...presetRows, ...customRows];
};

const slugFromPresetRowId = (id: string): string | null =>
  id.startsWith('preset-') ? id.slice('preset-'.length) : null;

export const FormFieldsSection = ({
  value,
  onChange,
  open,
  onToggleOpen,
  disabled = false,
  presets,
  isPhoneLockedRequired,
  dataTestId = FORM_FIELDS_SECTION_TEST_ID,
}: FormFieldsSectionProps) => {
  const { t } = useTranslation();
  const isMobileViewport = useWindowSize(SM_VIEWPORT);
  const toggleSize: ToggleSectionSize = isMobileViewport ? TOGGLE_SECTION_SIZE_S : TOGGLE_SECTION_SIZE_L;

  const sectionTitle = t('offers:admissionCheckoutFormSectionTitle');
  const mainHolderTitle = t('offers:admissionTicketBookingFormFieldsLabel');
  const mainHolderTooltip = t('offers:admissionTicketIsNominativeTooltip');
  const ticketHolderTitle = t('offers:admissionTicketHolderFormFieldsLabel');
  const nominativeLabel = t('offers:admissionNominativeEnforcedToggleLabel');
  const nominativeSubLabel = t('offers:admissionNominativeEnforcedToggleSubLabel');

  const nameAndSurnameLabel = t('offers:admissionFormFieldNameAndSurname');
  const emailLabel = t('common:emailFormFieldLabel');
  const phoneLabel = t('common:phoneFormFieldLabel');
  const customFieldDefaultName = t('offers:admissionFormFieldNamePlaceholder');

  const tableCopy = {
    tableHeaderLabel: t('offers:admissionFormFieldTableHeader'),
    collectedCountLabel: ({ collected, total }: { collected: number; total: number }) =>
      t('offers:admissionFormFieldTableCollectedCount', { collected, total }),
    addCustomFieldLabel: t('offers:admissionFormFieldAddCustomField'),
    requirementLabels: {
      off: t('offers:admissionFormFieldRequirementOff'),
      optional: t('offers:admissionFormFieldRequirementOptional'),
      required: t('offers:admissionFormFieldRequirementRequired'),
    },
    typeLabels: {
      text: t('offers:textFormfieldLabel'),
      number: t('offers:numericFormfieldLabel'),
    },
    namePlaceholder: customFieldDefaultName,
    minPlaceholder: t('offers:admissionFormFieldMinPlaceholder'),
    maxPlaceholder: t('offers:admissionFormFieldMaxPlaceholder'),
    saveLabel: t('offers:confirmSaleButtonText'),
    cancelLabel: t('common:cancel'),
    deleteConfirm: {
      title: t('offers:admissionFormFieldDeleteConfirmTitle'),
      description: t('offers:admissionFormFieldDeleteConfirmDescription'),
      confirmText: t('common:delete'),
      cancelText: t('common:cancel'),
    },
    mobileModalCopy: {
      customTitle: t('offers:admissionFormFieldCustomFieldTitle'),
      nameLabel: t('common:nameLabel'),
      typeLabel: t('offers:typeFieldLabel'),
      deleteFieldLabel: t('offers:admissionFormFieldDeleteField'),
    },
  };

  const updateGroup = (key: GroupKey, patch: Partial<FormFieldGroupValue>) => {
    const next: FormFieldGroupValue = { ...value[key], ...patch };
    onChange({ [key]: next });
  };

  const buildHandlers = (key: GroupKey) => {
    const group = value[key];

    const handleRequirementChange = (rowId: string, nextRequirement: FormFieldRowRequirement) => {
      const slug = slugFromPresetRowId(rowId);
      if (slug) {
        updateGroup(key, {
          presetRequirements: { ...group.presetRequirements, [slug]: nextRequirement },
        });
        return;
      }
      updateGroup(key, {
        customFields: group.customFields.map((row) =>
          row.id === rowId ? { ...row, requirement: nextRequirement } : row
        ),
      });
    };

    const handleCustomFieldSave = (rowId: string, nextValue: FormFieldRowValue) => {
      updateGroup(key, {
        customFields: group.customFields.map((row) => (row.id === rowId ? { ...row, value: nextValue } : row)),
      });
    };

    const handleCustomFieldDelete = (rowId: string) => {
      updateGroup(key, { customFields: group.customFields.filter((row) => row.id !== rowId) });
    };

    const handleAddCustomField = (): string => {
      const fresh: FormFieldsSectionCustomField = {
        id: nextCustomFieldId(),
        value: { type: 'text', name: '' },
        requirement: 'optional',
      };
      updateGroup(key, { customFields: [...group.customFields, fresh] });
      return fresh.id;
    };

    return { handleRequirementChange, handleCustomFieldSave, handleCustomFieldDelete, handleAddCustomField };
  };

  const mainHandlers = buildHandlers('mainHolder');
  const ticketHandlers = buildHandlers('ticketHolder');

  const mainHolderLockedSlugs = new Set<string>([SLUG_EMAIL, ...(isPhoneLockedRequired ? [SLUG_PHONE] : [])]);

  const mainHolderRows = buildRows({
    group: value.mainHolder,
    presets,
    nameAndSurnameLabel,
    emailLabel,
    phoneLabel,
    lockedSlugs: mainHolderLockedSlugs,
  });

  const ticketHolderRows = buildRows({
    group: value.ticketHolder,
    presets,
    nameAndSurnameLabel,
    emailLabel,
    phoneLabel,
    lockedSlugs: TICKET_HOLDER_LOCKED_SLUGS,
  });

  return (
    <SectionDisclosure
      title={sectionTitle}
      open={open}
      onToggleOpen={onToggleOpen}
      dataTestId={tid(dataTestId, 'disclosure')}>
      <section className='flex w-full flex-col gap-6 py-4 md:py-6' data-testid={dataTestId}>
        <div className='flex flex-col gap-3' data-testid={tid(dataTestId, 'main-holder')}>
          <LabelInfo text={mainHolderTooltip} ariaLabel={mainHolderTitle} marginBottomClassName=''>
            {mainHolderTitle}
          </LabelInfo>
          <FormFieldTable
            rows={mainHolderRows}
            onRequirementChange={mainHandlers.handleRequirementChange}
            onCustomFieldSave={mainHandlers.handleCustomFieldSave}
            onCustomFieldDelete={mainHandlers.handleCustomFieldDelete}
            onAddCustomField={mainHandlers.handleAddCustomField}
            disabled={disabled}
            modalZIndex={ADMISSION_DRAWER_OVERLAY_Z_INDEX}
            modalSurfaceClassName={ADMISSION_DRAWER_SURFACE_CLASSNAME}
            dataTestId={tid(dataTestId, 'main-holder-table')}
            {...tableCopy}
          />
        </div>

        <ToggleSection
          value={value.isNominative}
          onChange={(isNominative) => onChange({ isNominative })}
          label={nominativeLabel}
          subLabel={nominativeSubLabel}
          size={toggleSize}
          disabled={disabled}
          dataTestId={tid(dataTestId, 'nominative-toggle')}>
          <div className='flex flex-col gap-3' data-testid={tid(dataTestId, 'ticket-holder')}>
            <h3 className='text-body-xs text-primary'>{ticketHolderTitle}</h3>
            <FormFieldTable
              rows={ticketHolderRows}
              onRequirementChange={ticketHandlers.handleRequirementChange}
              onCustomFieldSave={ticketHandlers.handleCustomFieldSave}
              onCustomFieldDelete={ticketHandlers.handleCustomFieldDelete}
              onAddCustomField={ticketHandlers.handleAddCustomField}
              disabled={disabled}
              modalZIndex={ADMISSION_DRAWER_OVERLAY_Z_INDEX}
              modalSurfaceClassName={ADMISSION_DRAWER_SURFACE_CLASSNAME}
              dataTestId={tid(dataTestId, 'ticket-holder-table')}
              {...tableCopy}
            />
          </div>
        </ToggleSection>
      </section>
    </SectionDisclosure>
  );
};
